import { useEffect, useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { OpenInNewOutlined } from "@mui/icons-material";
import { useAuthStore } from "../../stores/authStore";
import { portalLoginUrl } from "../../utils/portal";

/** Cada cuánto, como mucho, se le pregunta al servidor al volver a la pestaña. */
const MIN_ENTRE_VERIFICACIONES_MS = 60_000;

function urlLogin(): string {
  const base: string = (import.meta as any).env?.BASE_URL || "/";
  return portalLoginUrl() ?? `${base.replace(/\/$/, "")}/login`;
}

/**
 * La sesión se cerró con la app abierta.
 *
 * Antes la interfaz no se enteraba: preguntaba por la sesión solo al cargar la página,
 * así que seguía mostrando a la persona como conectada y lo que intentaba guardar —una
 * evaluación recién escrita— fallaba con un error que no explicaba nada.
 *
 * Ahora se entera de dos formas: cualquier 401 del backend (el interceptor de axios) y
 * una pregunta al volver a la pestaña, que es cuando la sesión suele haber caducado
 * —se dejó abierta de un día para otro—. Así el aviso aparece antes de que la persona
 * se ponga a escribir, y no después.
 *
 * **No manda al login.** Recargar o navegar se llevaría lo que hay en pantalla. El
 * login se abre en otra pestaña; al volver, se verifica solo y el aviso se cierra, y lo
 * escrito sigue ahí para enviarlo de nuevo.
 */
export default function SesionExpirada() {
  const { sesionExpirada, verificarSesion } = useAuthStore();
  const [verificando, setVerificando] = useState(false);
  const [sigueSinSesion, setSigueSinSesion] = useState(false);

  useEffect(() => {
    let ultima = Date.now();
    const alVolver = () => {
      if (document.visibilityState !== "visible") return;
      // Con el aviso abierto se pregunta siempre: es lo que lo cierra al volver del login.
      if (!useAuthStore.getState().sesionExpirada && Date.now() - ultima < MIN_ENTRE_VERIFICACIONES_MS) return;
      ultima = Date.now();
      verificarSesion();
    };
    document.addEventListener("visibilitychange", alVolver);
    window.addEventListener("focus", alVolver);
    return () => {
      document.removeEventListener("visibilitychange", alVolver);
      window.removeEventListener("focus", alVolver);
    };
  }, [verificarSesion]);

  useEffect(() => {
    if (!sesionExpirada) setSigueSinSesion(false);
  }, [sesionExpirada]);

  const yaEntre = async () => {
    setVerificando(true);
    const ok = await verificarSesion();
    setVerificando(false);
    setSigueSinSesion(!ok);
  };

  return (
    <Dialog open={sesionExpirada} maxWidth="xs" fullWidth>
      <DialogTitle>Tu sesión se cerró</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Para guardar hay que volver a entrar. <strong>Lo que tienes en pantalla no se pierde</strong>: entra en otra
          pestaña, vuelve a esta y envíalo de nuevo.
        </DialogContentText>
        {sigueSinSesion && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Todavía no hay sesión. Entra en la otra pestaña y vuelve.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={yaEntre} disabled={verificando}>
          {verificando ? "Verificando…" : "Ya entré"}
        </Button>
        <Button variant="contained" startIcon={<OpenInNewOutlined />} href={urlLogin()} target="_blank" rel="noopener">
          Entrar en otra pestaña
        </Button>
      </DialogActions>
    </Dialog>
  );
}
